// Utility functions for handling subdomain-aware paths
import { store } from '../state/store';

/**
 * Check if a path is a mobile path
 */
export const isMobilePath = (path: string = window.location.pathname): boolean => {
  return path === '/m' || path.startsWith('/m/');
};

/**
 * Extract subdomain from current URL path
 * Handles both desktop and mobile paths
 * 
 * Examples:
 * - Desktop: /mystore/checkout -> mystore
 * - Mobile: /mystore/m/checkout -> mystore
 * 
 * Strategy:
 * 1. For mobile paths: If second segment is 'm', first segment is subdomain (deterministic)
 * 2. For desktop paths: Only return subdomain if first segment matches subdomain from Redux store
 *    - This prevents false positives like /my-account/profile being treated as subdomain
 */
export const getCurrentSubdomain = (pathname: string = window.location.pathname): string | null => {
  const parts = pathname.split('/').filter(Boolean);
  
  if (parts.length === 0) return null;
  
  // Check if first part is 'm' (mobile without subdomain)
  if (parts[0] === 'm') return null;
  
  // Check if second part is 'm' (mobile with subdomain: /mystore/m)
  // This is deterministic - if second segment is 'm', first is always subdomain
  if (parts.length >= 2 && parts[1] === 'm') {
    return parts[0];
  }
  
  // Desktop path: only return subdomain if first segment matches subdomain from store
  // This prevents multi-path routes like /my-account/profile from being treated as subdomain
  const subdomainFromStore = getSubdomainFromStore();
  if (subdomainFromStore && parts[0] === subdomainFromStore) {
    return parts[0];
  }
  
  // Don't assume first segment is subdomain if it doesn't match store
  // This prevents false positives for routes like /my-account/profile
  return null;
};

/**
 * Get subdomain from Redux store
 */
export const getSubdomainFromStore = (): string | null => {
  const state = store.getState();
  return (state as any).subdomain || null;
};

/**
 * Get relative path by removing subdomain if present
 * Handles both desktop and mobile paths
 * 
 * Examples:
 * - Desktop: /mystore/checkout -> /checkout
 * - Mobile: /mystore/m/checkout -> /m/checkout
 * 
 * @param pathname - The pathname to process (defaults to current location)
 * @param subdomain - Optional subdomain to remove (if not provided, extracts from path)
 * @returns Path without subdomain
 */
export const getRelativePath = (
  pathname: string = window.location.pathname,
  subdomain?: string | null
): string => {
  // If no subdomain provided, try to extract from path
  if (!subdomain) {
    subdomain = getCurrentSubdomain(pathname);
  }
  
  if (!subdomain) return pathname;
  
  const parts = pathname.split('/').filter(Boolean);
  
  // Mobile path with subdomain: /mystore/m/checkout -> /m/checkout
  if (parts.length >= 2 && parts[0] === subdomain && parts[1] === 'm') {
    if (parts.length === 2) return '/m'; // /mystore/m -> /m
    return '/m/' + parts.slice(2).join('/'); // /mystore/m/checkout -> /m/checkout
  }
  
  // Desktop path with subdomain: /mystore/checkout -> /checkout
  if (parts.length >= 1 && parts[0] === subdomain) {
    if (parts.length === 1) return '/'; // /mystore -> /
    return '/' + parts.slice(1).join('/'); // /mystore/checkout -> /checkout
  }
  
  return pathname;
};

/**
 * Prepend subdomain to path if present in Redux store
 * Handles both desktop and mobile paths, including query strings
 * 
 * Examples:
 * - Desktop: /checkout -> /mystore/checkout (if subdomain exists)
 * - Mobile: /m/checkout -> /mystore/m/checkout (if subdomain exists)
 * - With query: /payment-processing?ref=123 -> /mystore/payment-processing?ref=123
 * 
 * @param path - The path to prepend subdomain to (may include query string)
 * @returns Path with subdomain prepended, or original path if no subdomain in store
 */
export const getSubdomainPath = (path: string): string => {
  let subdomain = getSubdomainFromStore();
  
  // Fallback: If not in store (shouldn't happen, but defensive programming),
  // try to get from HTML data attribute as a last resort
  // This ensures the subdomain is available even if the Redux store is temporarily cleared
  if (!subdomain) {
    const rootElement = document.getElementById('root');
    if (rootElement) {
      const dataSubdomain = rootElement.dataset.subdomain;
      if (dataSubdomain && dataSubdomain !== 'undefined' && dataSubdomain !== '') {
        subdomain = dataSubdomain;
      }
    }
  }
  
  if (!subdomain) return path;
  
  // Split path and query string
  const [pathname, queryString] = path.split('?');
  const query = queryString ? `?${queryString}` : '';
  
  // Ensure pathname starts with /
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  
  // Check if this is a mobile path
  const isMobile = isMobilePath(normalizedPath);
  
  let subdomainPath: string;
  if (isMobile) {
    // Mobile path: /m/checkout -> /mystore/m/checkout
    // Remove /m prefix
    const pathWithoutM = normalizedPath === '/m' ? '' : normalizedPath.substring(2);
    subdomainPath = `/${subdomain}/m${pathWithoutM}`;
  } else {
    // Desktop path: /checkout -> /mystore/checkout
    subdomainPath = `/${subdomain}${normalizedPath === '/' ? '' : normalizedPath}`;
  }
  
  // Append query string if present
  return subdomainPath + query;
};

/**
 * Check if current path matches any of the given paths (subdomain-aware)
 */
export const isCurrentPath = (paths: string[], subdomain?: string | null): boolean => {
  const currentPath = window.location.pathname;
  const relativePath = getRelativePath(currentPath, subdomain);
  return paths.includes(relativePath);
};
